import { useCallback, useEffect, useRef, useState } from 'react';
import { describeError } from '../../../services/errorMessage';
import {
    aktuellesJwt,
    ladeKarte,
    ladeSitzung,
    melden,
    type MeldeSitzung,
} from '../../../services/material/materialPublicService';
import {
    NAME_SPEICHER_SCHLUESSEL,
    TEXTE,
    bereinigeName,
    zustandNachLaden,
    zustandNachSenden,
    type MeldenSchritt,
} from '../../../services/material/materialMeldenLogic';
import type { MaterialKarte } from '../../../services/material/materialTypes';

/**
 * Zustand und Handlungen der Meldeseite `<origin>/m/<token>`
 * (docs/materialverwaltung-plan.md, Client 3.).
 *
 * Lädt nur die Karte (anon-RPC) und — falls der Browser angemeldet ist — den
 * eigenen Namen aus dem Profil. Keine App-Daten. Der freiwillige Name liegt im
 * localStorage, damit die nächste Karte ihn schon kennt.
 */

const leseName = (): string => {
    try {
        return bereinigeName(localStorage.getItem(NAME_SPEICHER_SCHLUESSEL));
    } catch {
        // Privater Modus / gesperrter Speicher: die Seite läuft ohne Gedächtnis.
        return '';
    }
};

const merkeName = (name: string): void => {
    try {
        if (name) localStorage.setItem(NAME_SPEICHER_SCHLUESSEL, name);
        else localStorage.removeItem(NAME_SPEICHER_SCHLUESSEL);
    } catch {
        // s. o. — ein gesperrter Speicher darf die Meldung nicht stoppen.
    }
};

export const useMaterialMelden = (token: string | null) => {
    const [schritt, setSchritt] = useState<MeldenSchritt>(token ? 'laedt' : 'ungueltig');
    const [karte, setKarte] = useState<MaterialKarte | null>(null);
    /** Zeitpunkt des Stands — Bezug für „vor X Tagen“ und die Sperre. */
    const [jetzt, setJetzt] = useState(0);
    const [fehlerText, setFehlerText] = useState<string | null>(null);
    const [sitzung, setSitzung] = useState<MeldeSitzung | null>(null);
    const [name, setName] = useState(leseName);

    /**
     * Riegel gegen den zweiten Fingertipp auf „Bestätigen“: `schritt` steht
     * erst nach dem nächsten Render, zwei schnelle Berührungen liefen davor
     * beide durch (Muster useFreimeldung).
     */
    const laeuft = useRef(false);

    /** Nur das Ergebnis des zuletzt gestarteten Ladelaufs darf in den Zustand. */
    const ladeLauf = useRef(0);

    const montiert = useRef(true);
    useEffect(() => {
        montiert.current = true;
        return () => {
            montiert.current = false;
        };
    }, []);

    /** Karte und Sitzung holen. Setzt Zustand nur nach dem `await` (Effekt-Regel). */
    const holeStand = useCallback(async (): Promise<void> => {
        if (!token) return;
        const lauf = ++ladeLauf.current;
        try {
            const [k, s] = await Promise.all([ladeKarte(token), ladeSitzung()]);
            if (!montiert.current || lauf !== ladeLauf.current) return;
            const t = Date.now();
            setKarte(k);
            setSitzung(s);
            setJetzt(t);
            setFehlerText(null);
            setSchritt(zustandNachLaden(k, t));
        } catch (e) {
            if (!montiert.current || lauf !== ladeLauf.current) return;
            setFehlerText(TEXTE.ladeFehler(describeError(e)));
            setSchritt('fehler');
        }
    }, [token]);

    useEffect(() => {
        void holeStand();
    }, [holeStand]);

    // Tab-Titel: wer mehrere Karten scannt, findet den Tab wieder.
    const artikelName = karte?.artikel.name ?? '';
    useEffect(() => {
        document.title = artikelName ? `${TEXTE.seitenTitel}: ${artikelName}` : TEXTE.seitenTitel;
    }, [artikelName]);

    /**
     * „Erneut versuchen“ lädt den Stand neu, statt blind noch einmal zu
     * senden: Kam die erste Meldung doch an, zeigt die Seite „schon
     * ausgelöst“; sonst steht der Knopf wieder da.
     */
    const erneutVersuchen = useCallback(() => {
        if (laeuft.current) return;
        setFehlerText(null);
        setSchritt(token ? 'laedt' : 'ungueltig');
        void holeStand();
    }, [token, holeStand]);

    const bestaetigen = useCallback(async () => {
        if (laeuft.current || !token || schritt !== 'bereit') return;
        laeuft.current = true;
        setSchritt('sendet');
        try {
            // Mit Sitzung zählt auf dem Server das Profil; der Name im Rumpf
            // ist nur der Rückfall, falls das JWT inzwischen nicht mehr gilt.
            let melderName: string;
            let jwt: string | null = null;
            if (sitzung) {
                melderName = sitzung.name ?? '';
                jwt = await aktuellesJwt();
            } else {
                melderName = bereinigeName(name);
                merkeName(melderName);
            }
            const antwort = await melden(token, melderName, jwt);
            if (!montiert.current) return;
            const n = zustandNachSenden(antwort);
            setJetzt(Date.now());
            if (n.karte) setKarte(n.karte);
            setFehlerText(n.fehlerText);
            setSchritt(n.schritt);
        } catch (e) {
            if (!montiert.current) return;
            setFehlerText(describeError(e));
            setSchritt('fehler');
        } finally {
            laeuft.current = false;
        }
    }, [token, schritt, sitzung, name]);

    return {
        schritt,
        karte,
        jetzt,
        fehlerText,
        sitzung,
        name,
        setName,
        bestaetigen,
        erneutVersuchen,
    };
};

export type MaterialMeldenSteuerung = ReturnType<typeof useMaterialMelden>;
